import styled from 'styled-components'
import { colors } from '@monorepo/theme'

export const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`

export const PeriodLabel = styled.span`
  font-family: PFDINText;
  font-size: 13px;
  color: ${colors.mediumGrey};
`

export const Controls = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
`

export const DateRangeRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`

export const DateField = styled.div`
  display: flex;
  flex-direction: column;
`

export const DateLabel = styled.span`
  text-transform: uppercase;
  font-family: PFDINText;
  font-size: 10px;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
  color: ${colors.mediumGrey};
`

export const DateInput = styled.input`
  font-family: PFDINText;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  border: 0.3px solid ${colors.grey};
  border-radius: 6px;
  background: #ffffff;
  color: #333333;
`

export const PresetButton = styled.button<{ active?: boolean }>`
  font-family: PFDINText;
  font-size: 13px;
  min-width: 64px;
  height: 38px;
  padding: 8px 14px;
  border-radius: 4px;
  cursor: pointer;
  background-color: ${({ active }) => (active ? '#337ace' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#333333')};
  border: 1px solid ${({ active }) => (active ? '#337ace' : '#d5d5d5')};

  &:hover {
    border-color: #337ace;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }
`

export const ButtonSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  vertical-align: -2px;
  animation: tmz-dashboard-spin 0.7s linear infinite;

  @keyframes tmz-dashboard-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

/* ---- KPI cards ---- */

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-top: 4px solid #337ace;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const StatLabel = styled.span`
  font-family: PFDINText;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${colors.mediumGrey};
`

export const StatValue = styled.span`
  font-family: PFDINText;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
  color: #222222;
`

export const DeltaRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 14px;
  margin-top: 2px;
`

export const Delta = styled.span<{ direction: 'up' | 'down' | 'flat' }>`
  font-family: PFDINText;
  font-size: 12px;
  font-weight: 700;
  color: ${({ direction }) =>
    direction === 'up'
      ? '#00a651'
      : direction === 'down'
      ? '#c0392b'
      : '#999999'};
`

export const DeltaCaption = styled.span`
  font-family: PFDINText;
  font-size: 11px;
  font-weight: normal;
  color: ${colors.mediumGrey};
`

/* Plain-language definition of the metric, so nobody has to guess what it counts. */
export const StatHint = styled.span`
  font-family: PFDINText;
  font-size: 10.5px;
  line-height: 1.35;
  color: ${colors.mediumGrey};
  margin-top: 4px;
`

/* A tile holding two related numbers, e.g. print & ship vs downloads. */
export const SplitStatCard = styled(StatCard)`
  gap: 10px;
`

export const SplitRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
`

export const SplitItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const SplitValue = styled.span`
  font-family: PFDINText;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: #222222;
`

export const SplitLabel = styled.span`
  font-family: PFDINText;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${colors.mediumGrey};
`

/* A dashboard section (Active Users / Orders / Dealer Engagement). Wrapped in
   its own block so the PDF export can capture one section per landscape page.
   White background so html2canvas renders it cleanly against the page. */
export const SectionBlock = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  box-sizing: border-box;
  padding: 15px;
`

export const GroupTitle = styled.h2`
  font-family: PFDINText;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.mediumGrey};
  margin-top: 4px;
`

export const StatRowThree = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

export const ExportButton = styled.button`
  font-family: PFDINText;
  font-size: 13px;
  padding: 9px 16px;
  border-radius: 4px;
  cursor: pointer;
  background-color: #ffffff;
  color: #337ace;
  border: 1px solid #337ace;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  &:hover {
    background-color: #337ace;
    color: #ffffff;
  }

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }
`

/* ---- chart cards ---- */

export const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(560px, 2fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`

export const ChartCard = styled.div`
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const ChartTitle = styled.h2`
  font-family: PFDINText;
  font-size: 15px;
  font-weight: 500;
`

export const ChartSubtitle = styled.span`
  font-family: PFDINText;
  font-size: 11px;
  color: ${colors.mediumGrey};
`

/* nivo needs an explicit height on the wrapper */
export const ChartBody = styled.div<{ height?: number }>`
  position: relative;
  height: ${({ height }) => height || 260}px;
  width: 100%;
`

/* Caveat pinned under a chart, keyed to an asterisk in its subtitle. */
export const ChartFootnote = styled.span`
  font-family: PFDINText;
  font-size: 10.5px;
  line-height: 1.3;
  color: ${colors.mediumGrey};
  margin-top: 6px;
`

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-family: PFDINText;
  font-size: 13px;
  color: ${colors.mediumGrey};
`

/* Shown when one or more sections failed, so a partial dashboard is never
   mistaken for a complete one. */
export const Warning = styled.div`
  font-family: PFDINText;
  font-size: 12px;
  color: #8a6100;
  background: #fff8e5;
  border: 1px solid #f0d492;
  border-radius: 4px;
  padding: 10px 12px;
`

export const Note = styled.p`
  font-family: PFDINText;
  font-size: 11px;
  color: ${colors.mediumGrey};
  font-style: italic;
`
